import { CheckCircle2, MessageSquareText, XCircle } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import type { Prontidao } from '@/api/secretario';

interface ProntidaoChecklistProps {
  prontidao: Prontidao;
  /** Com o Secretário JÁ ativo, a confirmação "tudo pronto" é redundante
   * (o passo já mostra "Secretário ativo") — oculta; pendências continuam. */
  ocultarConfirmacao?: boolean;
}

/**
 * Checklist de prontidão do Secretário (PLANO §9). As pendências globais são o
 * que impede a ativação; abaixo, o que cada área faz — fechar contrato ou o
 * pré-atendimento —, que não impede nada (docs/PLANO_SECRETARIO_ATIVACAO_SIMPLES.md).
 * É o backend que decide; aqui só apresentamos.
 */
export function ProntidaoChecklist({ prontidao, ocultarConfirmacao = false }: ProntidaoChecklistProps) {
  return (
    <div className="space-y-3">
      {prontidao.pronta ? (
        !ocultarConfirmacao && (
          <Alert className="border-emerald-500/30 bg-emerald-500/5">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <AlertDescription className="text-emerald-700">
              Tudo pronto. Você já pode ativar o Secretário.
            </AlertDescription>
          </Alert>
        )
      ) : (
        <ul className="space-y-2">
          {prontidao.pendencias.map((p) => (
            <li key={p.chave} className="flex items-start gap-2 text-sm">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <span className="text-muted-foreground">{p.descricao}</span>
            </li>
          ))}
        </ul>
      )}

      {prontidao.por_area.length > 0 && (
        <ul className="divide-y rounded-lg border">
          {prontidao.por_area.map((area) => (
            <li key={area.area_id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="min-w-0 break-words font-medium">{area.nome}</span>
              {area.pronta ? (
                <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-emerald-600">
                  <CheckCircle2 size={14} /> Fecha contrato
                </span>
              ) : (
                <span
                  className="flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground"
                  title={area.pendencias.map((p) => p.descricao).join(' · ')}
                >
                  <MessageSquareText size={14} /> Pré-atendimento
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
